/**
 * Recursive Transaction Service Module
 * Handles recurring transactions including creation, updates, enabling/disabling,
 * and retrieval of recurring transaction records.
 */

import Parse from "./parse.service";
import { auth, api, accountAPI } from "services";

const Recurring = Parse.Object.extend("Recurring");

/**
 * Creates a new recurring transaction
 * @param {string} accountId - ID of the account for the recurring transaction
 * @param {number} amount - Amount of the recurring transaction
 * @param {string} detail - Description of the recurring transaction
 * @param {Date} date - Start date of the recurring transaction
 * @param {string} interval - Frequency interval of the transaction
 * @param {Date} nextTriggerDate - Next date when the transaction should occur
 * @returns {Promise<Parse.Object>} Created recurring transaction object
 */
export const addRecursiveTxn = async (
  accountId,
  amount,
  detail,
  date,
  interval,
  nextTriggerDate
) => {
  const recurring = new Recurring();

  const myAccount = await accountAPI.getAccountById(accountId);
  recurring.set("accountId", myAccount);
  recurring.set("userId", auth.fetchUser());
  recurring.set("amount", parseInt(amount, 10));
  recurring.set("date", date);
  recurring.set("enabled", true);
  recurring.set("detail", detail);
  recurring.set("interval", interval);
  recurring.set("nextTriggerDate", nextTriggerDate);
  return await recurring.save();
};

/**
 * Finds a recurring transaction by ID
 * @param {string} id - ID of the recurring transaction
 * @returns {Promise<Parse.Object|string>} Recurring transaction object or error message
 */
export const findRecursiveTxn = async (id) => {
  try{
    const query = new Parse.Query(Recurring);
    query.limit(1);
    query.equalTo("objectId", id);
    const results = await query.find();
    return results[0];
  } catch(err) {
    return err.message;
  }
}

/**
 * Updates an existing recurring transaction
 * @param {string} id - ID of the recurring transaction to update
 * @param {number} amount - New amount
 * @param {string} detail - New description
 * @param {Date} date - New start date
 * @param {string} interval - New frequency interval
 * @param {Date} nextTriggerDate - New next trigger date
 * @returns {Promise<Parse.Object>} Updated recurring transaction object
 */
export const editRecursiveTxn = async (
  id,
  amount,
  detail,
  date,
  interval,
  nextTriggerDate
) => {
  const txn = await findRecursiveTxn(id);
  txn.set("amount", amount);
  txn.set("detail", detail);
  txn.set("date", date);
  txn.set("interval", interval);
  txn.set("nextTriggerDate", nextTriggerDate);
  return await txn.save();
};

/**
 * Permanently deletes a recurring transaction
 * @param {Parse.Object} txn - Recurring transaction object to delete
 * @returns {Promise<Parse.Object>} Result of the delete operation
 */
export const deleteRecursiveTxn = async (txn) => {
  return txn.destroy();
};

/**
 * Enables a recurring transaction
 * @param {string} id - ID of the recurring transaction to enable
 * @returns {Promise<Parse.Object|Error>} Updated transaction object or error
 */
export const enableRecursiveTxn = async (id) => {
  try {
    const txn = await findRecursiveTxn(id);
    txn.set("enabled", true);
    return await txn.save();
  } catch (error) {
    return error;
  }
};

/**
 * Disables a recurring transaction
 * @param {string} id - ID of the recurring transaction to disable
 * @returns {Promise<Parse.Object|Error>} Updated transaction object or error
 */
export const disableRecursiveTxn = async (id) => {
  try {
    const txn = await findRecursiveTxn(id);
    txn.set("enabled", false);
    return await txn.save();
  } catch (error) {
    return error;
  }
};

/**
 * Retrieves all recurring transactions for a specific account
 * @param {string} id - Account ID to fetch transactions for
 * @returns {Promise<Parse.Object[]>} Array of recurring transaction objects
 */
export const fetchRecursiveTxns = async (id) => {
  const myAccount = await accountAPI.getAccountById(id);
  const query = new Parse.Query(Recurring);
  query.limit(1000000);
  query.equalTo("accountId", myAccount);
  return await query.find();
};

/**
 * Retrieves all recurring transactions for the current user
 * @returns {Promise<Parse.Object[]>} Array of recurring transaction objects
 */
export const getAllRcTransactions = async () => {
  const query = new Parse.Query(Recurring);
  query.limit(1000000);
  query.ascending("date");
  query.equalTo("userId", auth.fetchUser());
  return await query.find();
};
